/**
 * /life-empowerment/* · LEARNING INSTITUTE FOR EMPOWERMENT LLC
 * Public B2B Marketing & Intake Portal
 * ───────────────────────────────────────────────────────────────────────────
 * 2026-02 LIGHT MODE pivot. Bright platinum background, deep navy type,
 * silver borders, brushed-gold + crimson accents. Sticky top nav, fluid
 * grid. The legal-identifier footer band is required on every page per
 * owner directive.
 *
 * Palette (locked):
 *   Platinum / White   #f8f9fa / #ffffff   (page background, cards)
 *   Deep Navy          #1f3a60             (primary type, CTA text)
 *   Charcoal           #212529             (body copy)
 *   Silver border      #dee2e6             (card / divider edges)
 *   Brushed Gold       #d4af37 → #c5a059   (CTAs, accents)
 *   Rich Crimson       #b7092b             (hover highlights, brand pulse)
 */

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import CookieConsentBanner from "../../components/life/CookieConsentBanner";
import { useGA4 } from "../../lib/useGA4";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

/* ────────────────────────────────────────────────────────────────────
   🛡️ LIFE_EMBLEM_URL — the L.I.F.E., LLC corporate medallion.
   ────────────────────────────────────────────────────────────────────
   The raw uploaded asset (`LOGO Reimagined.png`, 2816×1536) shipped
   with the medallion baked onto a textured cream background. On a
   light-mode site that cream rectangle reads as a "broken" frame
   around the badge. We solve that by serving a pre-processed,
   background-removed, tight-cropped variant from our own static
   /public folder — generated by /app/scripts/extract_life_emblem.py.
   The image is transparent-alpha PNG (1006×1024 @ 1.5 MB, cached).
   ──────────────────────────────────────────────────────────────────── */
export const LIFE_EMBLEM_URL = "/life-emblem.png";

export const LIFE_NAV = [
  { to: "/life-empowerment",              label: "Home",         end: true },
  { to: "/life-empowerment/about",        label: "About" },
  { to: "/life-empowerment/services",     label: "Services" },
  { to: "/life-empowerment/recognition",  label: "Recognition" },
  { to: "/life-empowerment/contact",      label: "Contact & Intake" },
];

export default function LifeEmpowermentLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const loc = useLocation();

  useGA4(loc.pathname);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/life-public/config`)
      .then((r) => alive && setCfg(r.data))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  return (
    <div
      className="life-shell min-h-screen flex flex-col font-sans selection:bg-[#d4af37] selection:text-[#1f3a60]"
      data-testid="life-public-root"
    >
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen}/>
      <main className="flex-1">
        <Outlet context={{ cfg }} />
      </main>
      <Footer cfg={cfg}/>
      <CookieConsentBanner />
    </div>
  );
}

/* ─── Header ──────────────────────────────────────────────────────────── */
function Header({ menuOpen, setMenuOpen }) {
  return (
    <header
      className="sticky top-0 z-40 bg-white/92 backdrop-blur border-b border-[#dee2e6] shadow-[0_1px_18px_rgba(31,58,96,0.08)]"
      data-testid="life-header"
    >
      <div className="h-[2px] bg-gradient-to-r from-[#b7092b] via-[#d4af37] to-[#b7092b]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
        <Link
          to="/life-empowerment"
          className="flex items-center gap-3 group"
          data-testid="life-header-brand"
        >
          <img
            src={LIFE_EMBLEM_URL}
            alt="Learning Institute for Empowerment corporate emblem"
            loading="eager"
            decoding="async"
            data-testid="life-header-emblem"
            className="w-auto object-contain group-hover:scale-[1.04] transition-transform"
            style={{
              maxHeight:     "50px",
              height:        "auto",
              objectFit:     "contain",
              imageRendering: "auto",
              filter:        "drop-shadow(0 2px 6px rgba(31,58,96,0.18))",
            }}
          />
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px] sm:text-base tracking-tight text-[#1f3a60]">
              L.I.F.E., LLC
            </div>
            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#b7092b]/85">
              Learning Institute · Empowerment
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="life-desktop-nav">
          {LIFE_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`life-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 font-mono text-[10.5px] tracking-[0.22em] uppercase transition-colors ${
                  isActive
                    ? "text-[#b7092b] border-b-2 border-[#b7092b]"
                    : "text-[#1f3a60]/85 hover:text-[#b7092b] border-b-2 border-transparent hover:border-[#d4af37]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/life-empowerment/contact"
            data-testid="life-header-cta"
            className="life-cta-gold ml-3 inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[9.5px] tracking-[0.24em] uppercase"
          >
            Secure Partnership <ArrowRight className="h-3.5 w-3.5"/>
          </Link>
        </nav>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          data-testid="life-mobile-menu-toggle"
          className="lg:hidden h-10 w-10 grid place-items-center text-[#1f3a60] hover:text-[#b7092b]"
        >
          {menuOpen ? <X className="h-6 w-6"/> : <Menu className="h-6 w-6"/>}
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-[#dee2e6] bg-white" data-testid="life-mobile-drawer">
          <nav className="px-4 py-3 flex flex-col">
            {LIFE_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `py-3 font-mono text-[11px] tracking-[0.22em] uppercase border-b border-[#e9ecef] transition-colors ${
                    isActive ? "text-[#b7092b]" : "text-[#1f3a60]/85 hover:text-[#b7092b]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/life-empowerment/contact"
              onClick={() => setMenuOpen(false)}
              className="life-cta-gold mt-3 inline-flex items-center justify-center gap-2 px-4 py-3 font-mono text-[10px] tracking-[0.24em] uppercase"
            >
              Secure Partnership <ArrowRight className="h-3.5 w-3.5"/>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ─── Footer ──────────────────────────────────────────────────────────── */
function Footer({ cfg }) {
  const ein = cfg?.ein || "92-1652917";
  const txid = cfg?.sc_transaction_id || "1197551";
  const legal = cfg?.legal_entity_name || "Learning Institute for Empowerment LLC";
  const addr = cfg?.address?.single_line || "7433 Springside Dr, Myrtle Beach, SC 29588";
  const email = cfg?.public_email || "hello@life-empowerment.com";

  return (
    <footer
      className="bg-white text-[#1f3a60] border-t border-[#dee2e6] mt-16"
      data-testid="life-footer"
    >
      <div className="h-[2px] bg-gradient-to-r from-[#b7092b] via-[#d4af37] to-[#b7092b]"/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={LIFE_EMBLEM_URL}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-auto object-contain"
              style={{
                maxHeight:     "50px",
                height:        "auto",
                objectFit:     "contain",
                imageRendering: "auto",
                filter:        "drop-shadow(0 2px 6px rgba(31,58,96,0.18))",
              }}
            />
            <div>
              <div className="font-display font-bold text-base text-[#1f3a60]">L.I.F.E., LLC</div>
              <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#b7092b]/85">
                Built Hometown · Built Right
              </div>
            </div>
          </div>
          <p className="text-[#212529]/85 text-sm leading-relaxed max-w-sm">
            Bridging community and commerce by highlighting the human element,
            then empowering it. A South Carolina domestic LLC delivering
            workforce-development and business-consulting services in Myrtle
            Beach and across the southeastern United States.
          </p>
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b] mb-3">Explore</div>
          <ul className="space-y-2 text-sm">
            {LIFE_NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-[#1f3a60]/85 hover:text-[#b7092b] transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
            <li className="pt-2 mt-2 border-t border-[#e9ecef]">
              <Link to="/life-empowerment/privacy" className="text-[#1f3a60]/60 hover:text-[#b7092b] text-xs">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/life-empowerment/terms" className="text-[#1f3a60]/60 hover:text-[#b7092b] text-xs">
                Terms of Service
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b] mb-3">
            Corporate Headquarters
          </div>
          <ul className="space-y-3 text-sm text-[#1f3a60]/85">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#d4af37] mt-0.5 shrink-0"/>
              <span data-testid="life-footer-address">{addr}</span>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#d4af37] shrink-0"/>
              <a href={`mailto:${email}`} className="hover:text-[#b7092b]">{email}</a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-[#d4af37] shrink-0"/>
              By appointment · please use intake form
            </li>
          </ul>
        </div>
      </div>

      {/* Legal-identifier band — required on every page */}
      <div className="border-t border-[#dee2e6] py-4 bg-[#f8f9fa]">
        <div
          data-testid="life-footer-legal-stamp"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[10px] md:text-[11px] text-[#1f3a60]/65 font-mono tracking-[0.18em] uppercase leading-relaxed"
        >
          <div>
            © {new Date().getFullYear()} {legal}. All rights reserved.
          </div>
          <div className="text-[#1f3a60]">
            {legal} is a legally registered South Carolina Domestic LLC
            (Transaction ID: <span className="text-[#b7092b] font-semibold">{txid}</span>)
            <span className="px-2 text-[#1f3a60]/35">|</span>
            EIN: <span className="text-[#b7092b] font-semibold">{ein}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
